import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
} from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import SignalFeed from '../components/ExceptionWorkspace/SignalFeed';
import ExceptionCaseQueue from '../components/ExceptionWorkspace/ExceptionCaseQueue';
import ExceptionCaseDetail from '../components/ExceptionWorkspace/ExceptionCaseDetail';
import { exceptionService } from '../api/exceptionService';
import {
  Signal,
  ExceptionCase,
  TimelineEvent,
  WorkflowType,
  mockEntities,
} from '../data/mockExceptionData';
import { vikMockEntities } from '../data/mockVASExceptionData';
import { scMockEntities } from '../data/mockSupplyChainExceptionData';
import styles from '../components/ExceptionWorkspace/ExceptionWorkspace.module.css';

// Merge entity graphs from both data sources
const allMockEntities = { ...mockEntities, ...vikMockEntities, ...scMockEntities };

const WORKFLOW_FILTERS: { label: string; value: WorkflowType | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'PO-to-Payment', value: 'po_to_payment_exception' },
  { label: 'Inbox AP', value: 'inbox_ap_exception' },
  { label: 'Customer Order Risk', value: 'customer_order_risk' },
  { label: 'Procurement', value: 'procurement_exception' },
  { label: 'VIK At-Risk', value: 'vik_at_risk_exception' },
];

// Supply chain sub-filters match on case.attributes.category
type SCTypeFilter = 'all_sc' | 'logistics' | 'procurement';

const SC_TYPE_FILTERS: { label: string; value: SCTypeFilter }[] = [
  { label: 'All Supply Chain', value: 'all_sc' },
  { label: 'Logistics', value: 'logistics' },
  { label: 'Procurement', value: 'procurement' },
];

type VASTypeFilter = 'all_vas' | 'vik' | 'merchant_incentives' | 'partner_credits' | 'rebates';

const VAS_TYPE_FILTERS: { label: string; value: VASTypeFilter }[] = [
  { label: 'All VAS', value: 'all_vas' },
  { label: 'VIK', value: 'vik' },
  { label: 'Merchant Incentives', value: 'merchant_incentives' },
  { label: 'Partner Credits', value: 'partner_credits' },
  { label: 'Rebates', value: 'rebates' },
];

const ExceptionWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const domain = searchParams.get('domain');
  const type = searchParams.get('type');
  const caseIdParam = searchParams.get('caseId');

  const [signals, setSignals] = useState<Signal[]>([]);
  const [cases, setCases] = useState<ExceptionCase[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<ExceptionCase | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [workflowFilter, setWorkflowFilter] = useState<WorkflowType | 'all'>('all');

  // Load signals and cases
  useEffect(() => {
    exceptionService.fetchSignals().then(setSignals);
    exceptionService.fetchCases().then(res => setCases(res.cases));
  }, []);

  // Auto-select case when caseId param is present
  useEffect(() => {
    if (caseIdParam) {
      setSelectedCaseId(caseIdParam);
    }
  }, [caseIdParam]);

  // Load case detail when selected
  useEffect(() => {
    if (!selectedCaseId) { setSelectedCase(null); setTimeline([]); return; }
    exceptionService.fetchCase(selectedCaseId).then(setSelectedCase);
    exceptionService.fetchTimeline(selectedCaseId).then(res => setTimeline(res.events));
  }, [selectedCaseId]);

  const handleCreateCase = useCallback(async (signalId: string) => {
    const newCase = await exceptionService.createCaseFromSignal(signalId);
    if (newCase) {
      setCases(prev => [newCase, ...prev]);
      setSelectedCaseId(newCase.case_id);
    }
  }, []);

  const handleStartWorkflow = useCallback(async () => {
    if (!selectedCaseId) return;
    await exceptionService.startWorkflow(selectedCaseId);
    // Refresh case
    const updated = await exceptionService.fetchCase(selectedCaseId);
    if (updated) setSelectedCase(updated);
  }, [selectedCaseId]);

  // Filter cases by domain, type, and workflow filter
  const filteredCases = useMemo(() => {
    let result = cases;
    if (domain) {
      result = result.filter(c => c.domain === domain);
    }
    if (type && type !== 'all_vas' && type !== 'all_sc') {
      result = result.filter(
        c => c.workflow_type === type ||
          c.workflow_type === `${type}_at_risk_exception` ||
          c.attributes?.commitment_type === type ||
          c.attributes?.category === type
      );
    }
    if (!domain && workflowFilter !== 'all') {
      result = result.filter(c => c.workflow_type === workflowFilter);
    }
    return result;
  }, [cases, domain, type, workflowFilter]);

  const handleVASTypeFilter = (value: VASTypeFilter | SCTypeFilter) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all_vas' || value === 'all_sc') {
      newParams.delete('type');
    } else {
      newParams.set('type', value);
    }
    setSearchParams(newParams);
  };

  // Filter signals by domain when domain param is active
  const filteredSignals = useMemo(() => {
    let result = signals;
    if (domain) {
      result = result.filter(s => s.domain === domain);
    }
    if (domain === 'supply_chain' && (type === 'logistics' || type === 'procurement')) {
      result = result.filter(s => s.attributes?.category === type);
    }
    return result;
  }, [signals, domain, type]);

  const entities = selectedCaseId ? (allMockEntities[selectedCaseId] || { nodes: [], edges: [] }) : { nodes: [], edges: [] };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        {domain === 'vas' ? (
          <Breadcrumb aria-label="Navigation">
            <BreadcrumbItem>
              <BreadcrumbButton onClick={() => navigate('/domains/vas')}>
                VAS Command Center
              </BreadcrumbButton>
            </BreadcrumbItem>
            <BreadcrumbDivider />
            <BreadcrumbItem>
              <BreadcrumbButton current>
                VIK Exception Workspace
              </BreadcrumbButton>
            </BreadcrumbItem>
          </Breadcrumb>
        ) : domain === 'supply_chain' ? (
          <Breadcrumb aria-label="Navigation">
            <BreadcrumbItem>
              <BreadcrumbButton onClick={() => navigate('/domains/supply-chain')}>
                Supply Chain Command Center
              </BreadcrumbButton>
            </BreadcrumbItem>
            <BreadcrumbDivider />
            <BreadcrumbItem>
              <BreadcrumbButton current>
                Supply Chain Exception Workspace
              </BreadcrumbButton>
            </BreadcrumbItem>
          </Breadcrumb>
        ) : (
          <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={() => navigate('/domains')}>
            Back to Workflows
          </Button>
        )}
        <div className={styles.headerTitle}>Exception Workspace</div>
      </div>

      {/* Filter bar */}
      <div className={styles.filterBar}>
        {domain === 'vas' ? (
          VAS_TYPE_FILTERS.map(f => (
            <Badge
              key={f.value}
              size="medium"
              appearance={(!type && f.value === 'all_vas') || type === f.value ? 'filled' : 'outline'}
              color={(!type && f.value === 'all_vas') || type === f.value ? 'brand' : 'informative'}
              style={{ cursor: 'pointer' }}
              onClick={() => handleVASTypeFilter(f.value)}
            >
              {f.label}
            </Badge>
          ))
        ) : domain === 'supply_chain' ? (
          SC_TYPE_FILTERS.map(f => {
            const active = (!type && f.value === 'all_sc') || type === f.value;
            return (
              <Badge
                key={f.value}
                size="medium"
                appearance={active ? 'filled' : 'outline'}
                color={active ? 'brand' : 'informative'}
                style={{ cursor: 'pointer' }}
                onClick={() => handleVASTypeFilter(f.value)}
              >
                {f.label}
              </Badge>
            );
          })
        ) : (
          WORKFLOW_FILTERS.map(f => (
            <Badge
              key={f.value}
              size="medium"
              appearance={workflowFilter === f.value ? 'filled' : 'outline'}
              color={workflowFilter === f.value ? 'brand' : 'informative'}
              style={{ cursor: 'pointer' }}
              onClick={() => setWorkflowFilter(f.value)}
            >
              {f.label}
            </Badge>
          ))
        )}
      </div>

      {/* Body: left panel + right detail */}
      <div className={styles.body}>
        <div className={styles.leftPanel}>
          <SignalFeed signals={filteredSignals} onCreateCase={handleCreateCase} />
          <ExceptionCaseQueue cases={filteredCases} selectedCaseId={selectedCaseId} onSelectCase={setSelectedCaseId} />
        </div>
        <div className={styles.rightPanel}>
          {selectedCase ? (
            <ExceptionCaseDetail
              caseData={selectedCase}
              timeline={timeline}
              entities={entities}
              onStartWorkflow={handleStartWorkflow}
            />
          ) : (
            <div className={styles.detailEmpty}>Select a case to view details</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExceptionWorkspacePage;
